import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, PopConfirm, Select, Table, message } from '@maxtropy/components';
import { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import {
  apiV2AreaCameraBindPost,
  apiV2AreaCameraDetailPost,
  apiV2AreaCameraSelectPost,
} from '@maxtropy/device-customer-apis-v2';
import styles from './index.module.scss';

type AccessType = 'platform' | 'thirdParty';

interface VideoMonitorRow {
  id: string;
  accessType?: AccessType;
  accountId?: number;
  monitorDevice?: string;
  monitorDeviceName?: string;
  weight?: number;
}

interface VideoMonitorConfigModalProps {
  visible: boolean;
  areaId?: number | string;
  areaName?: string;
  onCancel: () => void;
}

interface DeviceOption {
  label: string;
  value: string;
  accountId?: number;
}

interface AccountOption {
  label: string;
  value: number;
}

const accessTypeOptions = [
  { label: '平台', value: 'platform' },
  { label: '第三方', value: 'thirdParty' },
];

const createEmptyRow = (): VideoMonitorRow => ({
  id: uuidv4(),
});

const VideoMonitorConfigModal: FC<VideoMonitorConfigModalProps> = ({ visible, areaId, onCancel }) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [platformAccountOptions, setPlatformAccountOptions] = useState<AccountOption[]>([]);
  const [platformDevicesByAccount, setPlatformDevicesByAccount] = useState<Record<number, DeviceOption[]>>({});
  const [thirdPartyDeviceOptions, setThirdPartyDeviceOptions] = useState<DeviceOption[]>([]);

  const initModalData = async () => {
    setLoadingOptions(true);
    try {
      const [platformRes, thirdPartyRes, detailRes] = await Promise.all([
        apiV2AreaCameraSelectPost({ type: 1 }),
        apiV2AreaCameraSelectPost({ type: 2 }),
        areaId != null && areaId !== '' ? apiV2AreaCameraDetailPost({ id: areaId }) : Promise.resolve({ list: [] }),
      ]);

      const accountOptions: AccountOption[] = [];
      const deviceMap: Record<number, DeviceOption[]> = {};

      (platformRes?.list || []).forEach(item => {
        if (item.accountId == null) {
          return;
        }
        accountOptions.push({
          label: item.accountName || String(item.accountId),
          value: item.accountId,
        });
        deviceMap[item.accountId] = (item.deviceList || []).map(device => ({
          label: device.deviceName || '',
          value: device.deviceId || '',
        }));
      });

      const thirdPartyOptions: DeviceOption[] = (thirdPartyRes?.list || [])
        .flatMap(item =>
          (item.deviceList || []).map(device => ({
            ...device,
            accountId: item.accountId,
          }))
        )
        .filter(device => !!device.deviceId)
        .map(device => ({
          label: device.deviceName || '',
          value: device.deviceId || '',
          accountId: device.accountId,
        }));

      setPlatformAccountOptions(accountOptions);
      setPlatformDevicesByAccount(deviceMap);
      setThirdPartyDeviceOptions(thirdPartyOptions);

      const detailRows: VideoMonitorRow[] = (detailRes?.list || []).map(item => ({
        id: uuidv4(),
        accessType: item.type === 2 ? 'thirdParty' : 'platform',
        accountId: item.accountId,
        monitorDevice: item.deviceId,
        monitorDeviceName: item.deviceNameAlias || '',
        weight: item.weighting,
      }));
      form.setFieldsValue({ list: detailRows.length ? detailRows : [createEmptyRow()] });
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    if (!visible) {
      return;
    }
    initModalData();
  }, [visible, areaId]);

  const addRow = () => {
    const currentRows = (form.getFieldValue('list') || []) as VideoMonitorRow[];
    form.setFieldsValue({
      list: [...currentRows, createEmptyRow()],
    });
  };

  const removeRow = (record: VideoMonitorRow) => {
    const currentRows = (form.getFieldValue('list') || []) as VideoMonitorRow[];
    form.setFieldsValue({
      list: currentRows.filter(item => item.id !== record.id),
    });
  };

  const getDeviceOptions = (index: number) => {
    const accessType = form.getFieldValue(['list', index, 'accessType']) as AccessType;
    if (accessType === 'thirdParty') {
      return thirdPartyDeviceOptions;
    }
    const accountId = form.getFieldValue(['list', index, 'accountId']) as number | undefined;
    if (!accountId) {
      return [];
    }
    return platformDevicesByAccount[accountId] || [];
  };

  const handleAccessTypeChange = (value: AccessType, index: number) => {
    const currentRows = (form.getFieldValue('list') || []) as VideoMonitorRow[];
    const currentRow = currentRows[index] || { id: uuidv4() };
    currentRows[index] = {
      ...currentRow,
      accessType: value,
      accountId: undefined,
      monitorDevice: undefined,
      monitorDeviceName: undefined,
    };
    form.setFieldsValue({ list: currentRows });
  };

  const handleAccountChange = (index: number) => {
    const currentRows = (form.getFieldValue('list') || []) as VideoMonitorRow[];
    const currentRow = currentRows[index] || { id: uuidv4() };
    currentRows[index] = {
      ...currentRow,
      monitorDevice: undefined,
      monitorDeviceName: undefined,
    };
    form.setFieldsValue({ list: currentRows });
  };

  const handleDeviceChange = (index: number, option: DeviceOption | DeviceOption[]) => {
    const selected = Array.isArray(option) ? option[0] : option;
    const accessType = form.getFieldValue(['list', index, 'accessType']) as AccessType;
    const currentRows = (form.getFieldValue('list') || []) as VideoMonitorRow[];
    const currentRow = currentRows[index] || { id: uuidv4() };
    currentRows[index] = {
      ...currentRow,
      accountId: accessType === 'thirdParty' ? selected?.accountId : currentRow.accountId,
      monitorDeviceName: selected?.label || '',
    };
    form.setFieldsValue({ list: currentRows });
    form.validateFields([['list', index, 'monitorDeviceName']]).catch(() => undefined);
  };

  const validateUniqueDeviceName = (index: number, value?: string) => {
    const name = (value || '').trim();
    if (!name) {
      return Promise.resolve();
    }
    const rows = (form.getFieldValue('list') || []) as VideoMonitorRow[];
    const duplicated = rows.some((row, rowIndex) => {
      if (rowIndex === index) {
        return false;
      }
      return (row.monitorDeviceName || '').trim() === name;
    });
    if (duplicated) {
      return Promise.reject(new Error('监控设备名称重复'));
    }
    return Promise.resolve();
  };

  const onSave = async () => {
    if (areaId == null || areaId === '') {
      message.error('区域ID缺失，无法保存视频监控配置');
      return;
    }
    await form.validateFields();
    setSaving(true);
    try {
      const rows = ((form.getFieldValue('list') || []) as VideoMonitorRow[]).filter(row => row.monitorDevice);
      let hasSaveError = false;
      await apiV2AreaCameraBindPost({
        id: areaId,
        deviceList: rows.map(row => ({
          type: row.accessType === 'thirdParty' ? 2 : 1,
          accountId: row.accountId,
          deviceId: row.monitorDevice,
          weighting: row.weight,
          deviceNameAlias: row.monitorDeviceName?.trim(),
        })),
      }).onError(() => {
        hasSaveError = true;
        return true;
      });
      if (hasSaveError) {
        message.error('保存失败，请稍后重试');
        return;
      }
      message.success('保存成功');
      onCancel();
    } catch (error) {
      message.error('保存失败，请稍后重试');
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<VideoMonitorRow> = useMemo(
    () => [
      {
        title: '接入方式',
        dataIndex: 'accessType',
        width: 120,
        render: (_, __, index) => (
          <Form.Item
            name={['list', index, 'accessType']}
            rules={[{ required: true, message: '请选择接入方式' }]}
            style={{ marginBottom: 0 }}
          >
            <Select
              options={accessTypeOptions}
              placeholder="请选择"
              onChange={value => handleAccessTypeChange(value as AccessType, index)}
            />
          </Form.Item>
        ),
      },
      {
        title: '账户名称',
        dataIndex: 'accountId',
        width: 140,
        render: (_, __, index) => (
          <Form.Item noStyle shouldUpdate={(prev: any, next: any) => prev?.list !== next?.list}>
            {() => {
              const accessType = form.getFieldValue(['list', index, 'accessType']) as AccessType;
              if (accessType === 'thirdParty') {
                return <span className={styles.dashText}>-</span>;
              }
              return (
                <Form.Item
                  name={['list', index, 'accountId']}
                  rules={[{ required: true, message: '请选择账户名称' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    placeholder="请选择"
                    showSearch
                    optionFilterProp="label"
                    options={platformAccountOptions}
                    onChange={() => handleAccountChange(index)}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
        ),
      },
      {
        title: '监控设备',
        dataIndex: 'monitorDevice',
        width: 180,
        render: (_, __, index) => (
          <Form.Item
            name={['list', index, 'monitorDevice']}
            rules={[{ required: true, message: '请选择监控设备' }]}
            style={{ marginBottom: 0 }}
          >
            <Select
              placeholder="请选择"
              showSearch
              optionFilterProp="label"
              options={getDeviceOptions(index)}
              onChange={(_, option) => handleDeviceChange(index, option as DeviceOption | DeviceOption[])}
            />
          </Form.Item>
        ),
      },
      {
        title: '监控设备名称',
        dataIndex: 'monitorDeviceName',
        width: 180,
        render: (_, __, index) => (
          <Form.Item
            name={['list', index, 'monitorDeviceName']}
            validateTrigger={['onChange']}
            rules={[
              { required: true, message: '请输入监控设备名称' },
              {
                validator: async (_, value) => validateUniqueDeviceName(index, value),
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input placeholder="请输入" maxLength={20} />
          </Form.Item>
        ),
      },
      {
        title: '权重',
        dataIndex: 'weight',
        width: 120,
        render: (_, __, index) => (
          <Form.Item
            name={['list', index, 'weight']}
            rules={[{ required: true, message: '请输入权重' }]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber placeholder="请输入" min={0} max={9999} precision={0} style={{ width: '100%' }} />
          </Form.Item>
        ),
      },
      {
        title: '操作',
        dataIndex: 'operation',
        width: 80,
        render: (_, record) => (
          <PopConfirm title="是否确认删除?" placement="top" onConfirm={() => removeRow(record)}>
            <Button type="link" className={styles.deleteBtn}>
              删除
            </Button>
          </PopConfirm>
        ),
      },
    ],
    [form, platformAccountOptions, platformDevicesByAccount, thirdPartyDeviceOptions]
  );

  return (
    <Modal
      title="视频监控配置"
      open={visible}
      size="big"
      onCancel={onCancel}
      onOk={onSave}
      okText="保存"
      cancelText="取消"
      confirmLoading={saving}
      okButtonProps={{ disabled: loadingOptions }}
      className={styles.videoConfigModal}
      contentClassName={styles.videoConfigModal}
    >
      <Form form={form} layout="vertical" isOrigin={true}>
        <Form.Item name="list" valuePropName="dataSource" noStyle>
          <Table rowKey="id" columns={columns} pagination={false} loading={loadingOptions} />
        </Form.Item>
        <Button
          className={styles.addRowBtn}
          type="dashed"
          ghost
          icon={<PlusOutlined />}
          wrapClassName={styles.addRowWrap}
          style={{ width: '100%' }}
          disabled={loadingOptions}
          onClick={addRow}
        >
          新增视频监控
        </Button>
      </Form>
    </Modal>
  );
};

export default VideoMonitorConfigModal;
